import { FC, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { Grid } from '@mui/material';
import { MerchantTrialStatus } from 'types/graphql';

import { navigate, routes } from '@redwoodjs/router';

import { ConfirmationModal } from 'src/components/Modals';
import TableList from 'src/components/TableList/TableList';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { SERVICES_TABLE_HEADERS } from 'src/constants';
import { ROLE_PERMISSIONS } from 'src/constants/role-permissions';
import { CONTINUE_TRIAL_MERCHANT_SERVICE } from 'src/graphql/queries/merchantApplications.query';
import { GET_MERCHANT_TRIAL_STATUS } from 'src/graphql/queries/merchants.query';

interface ServiceProps {
    services: {
        id: number;
        tradingName: string;
        serviceType: string;
        product: string;
        createdAt: string;
        status: string;
        contractExpiry: string;
        promosText: string;
        promoExpiryDate: Date | null;
        trialEndDate: Date | null;
        trialCompletedAt: Date | null;
    }[];
    merchantId: number;
    merchantTradingName?: string;
}
const Service: FC<ServiceProps> = ({ services, merchantId, merchantTradingName }) => {
    const [modalOpen, setModalOpen] = useState<boolean>(false);

    const { loading, data: merchantTrialStatusResponse } = useQuery(GET_MERCHANT_TRIAL_STATUS, {
        variables: { merchantId },
        skip: !merchantId,
        onError: (error) => {
            return showErrorToast(error.message);
        },
    });

    const [continueServiceMutation] = useMutation(CONTINUE_TRIAL_MERCHANT_SERVICE);

    const tableData = services.map((service) => {
        return {
            ...service,
            promosText: service.promosText,
            promoExpiryDate: service.promoExpiryDate
                ? new Date(service.promoExpiryDate).toISOString()
                : '-',
            trialEndDate: service.trialCompletedAt ?? service.trialEndDate ?? '-',
        };
    });

    const merchantStatus: MerchantTrialStatus | undefined =
        merchantTrialStatusResponse?.merchantStatus ?? undefined;

    const handleViewService = ({ id }) => {
        navigate(`/service/view/${id}`);
    };

    return (
        <Grid
            sx={{
                display: 'flex',
                flexDirection: 'column',
                maxWidth: '100%',
                width: '100%',
            }}
        >
            <TableList
                data={tableData}
                headers={SERVICES_TABLE_HEADERS}
                addRecordButtonText="Add Application"
                addRecordSecondButtonText="Add Service"
                hasAddRecordSecondButtonPermission={ROLE_PERMISSIONS.addService}
                title="Roles List"
                addRecordCallBack={() => {}}
                addSecondRecordCallBack={() => {
                    if (loading) {
                        return;
                    }

                    if (!merchantStatus) {
                        return showErrorToast(
                            'Merchant trial status is not available. Refresh the page and try again.'
                        );
                    }

                    if (merchantStatus.onTrial) {
                        return setModalOpen(true);
                    }

                    navigate(routes.AddService({ merchantId }));
                }}
                handleViewRecord={handleViewService}
                deleteRecordCallBack={() => {}}
                enableFilter
                filterOptions={{}}
                handleCellClick={() => {}}
            />

            <ConfirmationModal
                isOpen={modalOpen}
                title={`${merchantTradingName} is currently on a trial`}
                message={`${merchantTradingName} is currently on a trial. Adding a new service to this merchant will end their trial, and they will no longer be able to cancel free of charge. Have you informed the merchant about this?`}
                confirmText="Yes"
                cancelText="No"
                onCancel={() => {
                    setModalOpen(false);
                }}
                onConfirm={async () => {
                    setModalOpen(false);

                    await Promise.all(
                        (merchantStatus?.trialServiceIds ?? []).map((serviceId) => {
                            return continueServiceMutation({ variables: { input: { serviceId } } });
                        })
                    );

                    showSuccessToast(
                        `The trial period for "${merchantTradingName}" has been ended.`
                    );

                    return navigate(routes.AddService({ merchantId }));
                }}
                modalWidth={'600px'}
            />
        </Grid>
    );
};

export default Service;
